import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api from "../lib/api";
import { PageBody, PageHeader } from "../components/Layout";
import EmptyState from "../components/EmptyState";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import {
    Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "../components/ui/select";
import { Badge } from "../components/ui/badge";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../components/ui/dialog";
import { toast } from "sonner";
import { Brain, ArrowsClockwise, Sparkle, FloppyDisk, BookOpen, Archive } from "@phosphor-icons/react";

const MODELS = [
    { value: "anthropic:claude-sonnet-4-5-20250929", label: "Claude Sonnet 4.5  (recommended)" },
    { value: "openai:gpt-5.2", label: "OpenAI GPT-5.2" },
    { value: "openai:gpt-5", label: "OpenAI GPT-5" },
    { value: "anthropic:claude-opus-4-5-20251101", label: "Claude Opus 4.5" },
    { value: "gemini:gemini-2.5-pro", label: "Gemini 2.5 Pro" },
];

function ListSection({ overline, title, items }) {
    if (!items || items.length === 0) return null;
    return (
        <section className="border border-border p-5 bg-card">
            <div className="overline mb-2">{overline}</div>
            <h3 className="font-display font-semibold text-base mb-3">{title}</h3>
            <ul className="space-y-2 text-sm">
                {items.map((it, i) => (
                    <li key={i} className="leading-relaxed border-l-2 border-border pl-3 py-0.5 hover:border-primary transition-colors">
                        {it}
                    </li>
                ))}
            </ul>
        </section>
    );
}

export default function Analyses() {
    const [params] = useSearchParams();
    const [analyses, setAnalyses] = useState([]);
    const [active, setActive] = useState(null);
    const [running, setRunning] = useState(false);
    const [model, setModel] = useState(MODELS[0].value);
    const [titleHint, setTitleHint] = useState("");
    const [open, setOpen] = useState(false);
    const [pendingIds, setPendingIds] = useState([]);
    const [saveOpen, setSaveOpen] = useState(false);
    const [insightTitle, setInsightTitle] = useState("");
    const [insightText, setInsightText] = useState("");

    const load = async () => {
        const r = await api.get("/analyses");
        setAnalyses(r.data);
        if (!active && r.data.length) setActive(r.data[0]);
    };

    useEffect(() => {
        load();
    }, []);

    useEffect(() => {
        // Two ingest paths:
        // 1. session-stash (new, default) — IDs were placed in sessionStorage by RawSignals
        //    to avoid URL-length truncation when many signals are selected.
        // 2. legacy URL param (?comment_ids=…) — retained for back-compat / sharing.
        const via = params.get("via");
        if (via === "session") {
            try {
                const raw = window.sessionStorage.getItem("haevn:pending-analyze-ids");
                if (raw) {
                    const ids = JSON.parse(raw);
                    if (Array.isArray(ids) && ids.length > 0) {
                        setPendingIds(ids);
                        setOpen(true);
                        window.sessionStorage.removeItem("haevn:pending-analyze-ids");
                        return;
                    }
                }
            } catch {
                // ignore — fall through to URL fallback
            }
        }
        const ids = params.get("comment_ids");
        if (ids) {
            setPendingIds(ids.split(",").filter(Boolean));
            setOpen(true);
        }
    }, [params]);

    const runAnalysis = async () => {
        if (pendingIds.length === 0) {
            toast.error("No comments selected");
            return;
        }
        setRunning(true);
        try {
            const r = await api.post("/analyses/run", {
                comment_ids: pendingIds,
                title: titleHint || "",
                model,
            });
            toast.success("Analysis complete");
            setOpen(false);
            setPendingIds([]);
            setTitleHint("");
            await load();
            setActive(r.data);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Analysis failed");
        } finally {
            setRunning(false);
        }
    };

    const generateContent = async () => {
        if (!active) return;
        try {
            await api.post("/content-ideas/from-analysis", { analysis_id: active.id });
            toast.success("Content ideas added — see Content Ideas page");
        } catch (e) {
            toast.error("Failed to generate content ideas");
        }
    };

    const saveInsight = async () => {
        if (!active) return;
        if (!insightTitle.trim() || !insightText.trim()) {
            toast.error("Title and observation are required");
            return;
        }
        try {
            await api.post("/insights", {
                title: insightTitle,
                insight_text: insightText,
                emotional_theme: (active.emotional_themes || [])[0] || "",
                supporting_patterns: active.hidden_patterns || [],
                related_analyses: [active.id],
                topic_tags: active.suggested_trend_tags || [],
            });
            toast.success("Insight saved to archive");
            setSaveOpen(false);
            setInsightTitle("");
            setInsightText("");
        } catch (e) {
            toast.error("Failed to save insight");
        }
    };

    const archive = async () => {
        if (!active) return;
        await api.post(`/analyses/${active.id}/archive`);
        toast.success("Analysis archived");
        await load();
    };

    return (
        <>
            <PageHeader
                overline="Processing Layer"
                title="Analyses"
                description="Where raw signals become structured cultural reads. Run Claude or GPT against selected comments and save what's worth keeping."
                testid="analyses-header"
                actions={
                    pendingIds.length > 0 && (
                        <Button data-testid="open-run-dialog" onClick={() => setOpen(true)} size="sm" className="rounded-sm">
                            <Brain size={14} weight="duotone" className="mr-1.5" />
                            Run on {pendingIds.length} signals
                        </Button>
                    )
                }
            />

            <PageBody>
                <div className="grid grid-cols-12 gap-6">
                    <aside data-testid="analyses-list" className="col-span-12 lg:col-span-3 border border-border">
                        <div className="px-3 py-2 overline border-b border-border bg-surface">Recent</div>
                        {analyses.length === 0 ? (
                            <p className="p-4 text-xs text-muted-foreground">No analyses yet.</p>
                        ) : (
                            <ul className="divide-y divide-border max-h-[70vh] overflow-y-auto">
                                {analyses.map((a) => (
                                    <li key={a.id}>
                                        <button
                                            data-testid={`analysis-item-${a.id}`}
                                            onClick={() => setActive(a)}
                                            className={`w-full text-left p-3 hover:bg-surface ${active?.id === a.id ? "bg-surface border-l-2 border-primary" : ""}`}
                                        >
                                            <div className="font-display font-semibold text-sm leading-tight line-clamp-2">{a.title || "Untitled"}</div>
                                            <div className="text-[10px] font-mono text-muted-foreground mt-1">
                                                {(a.created_at || "").slice(0, 10)} · {(a.comment_ids || []).length} signals
                                            </div>
                                        </button>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </aside>

                    <section className="col-span-12 lg:col-span-9">
                        {!active ? (
                            <EmptyState
                                title="No analysis yet"
                                description="Go to Raw Signals, select comments, and click Analyze."
                                testid="analyses-empty"
                            />
                        ) : (
                            <div data-testid="analysis-detail" className="space-y-5">
                                <div className="border border-border p-6 bg-card">
                                    <div className="flex items-start justify-between gap-4 flex-wrap">
                                        <div className="max-w-2xl">
                                            <div className="overline mb-2">{active.model_used || "model"}</div>
                                            <h2 className="font-display font-bold text-2xl tracking-tight leading-tight">
                                                {active.title}
                                            </h2>
                                            {active.summary && (
                                                <p className="text-muted-foreground mt-3 text-[15px] leading-relaxed">
                                                    {active.summary}
                                                </p>
                                            )}
                                            <div className="flex flex-wrap gap-1.5 mt-4">
                                                {(active.suggested_trend_tags || []).slice(0, 8).map((t) => (
                                                    <Badge key={t} variant="outline" className="rounded-sm font-mono text-[10px]">{t}</Badge>
                                                ))}
                                            </div>
                                        </div>
                                        <div className="flex flex-col gap-2">
                                            <Button data-testid="save-insight-btn" size="sm" variant="outline" className="rounded-sm" onClick={() => setSaveOpen(true)}>
                                                <FloppyDisk size={13} className="mr-1.5" /> Save Insight
                                            </Button>
                                            <Button data-testid="generate-content-btn" size="sm" className="rounded-sm" onClick={generateContent}>
                                                <Sparkle size={13} className="mr-1.5" /> Generate Content
                                            </Button>
                                            <Button data-testid="archive-analysis-btn" size="sm" variant="ghost" className="rounded-sm" onClick={archive}>
                                                <Archive size={13} className="mr-1.5" /> Archive
                                            </Button>
                                        </div>
                                    </div>
                                </div>

                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <ListSection overline="What people feel" title="Emotional Themes" items={active.emotional_themes} />
                                    <ListSection overline="Verbatim language" title="Repeated Phrases" items={active.repeated_phrases} />
                                    <ListSection overline="Underneath the words" title="Hidden Patterns" items={active.hidden_patterns} />
                                    <ListSection overline="Tensions" title="Contradictions" items={active.contradictions} />
                                    <ListSection overline="What's true now" title="Cultural Observations" items={active.cultural_observations} />
                                    <ListSection overline="Rising vocabulary" title="Emerging Language" items={active.emerging_language} />
                                    <ListSection overline="Worth keeping" title="Key Insights" items={active.key_insights} />
                                    <ListSection overline="Open with this" title="Suggested Hooks" items={active.hooks} />
                                    <ListSection overline="Visual long-form" title="Carousel Ideas" items={active.carousel_ideas} />
                                    <ListSection overline="Camera-on" title="Founder Video Ideas" items={active.founder_video_ideas} />
                                    <ListSection overline="Discussion prompts" title="Podcast Topics" items={active.podcast_prompts} />
                                </div>
                            </div>
                        )}
                    </section>
                </div>
            </PageBody>

            <Dialog open={open} onOpenChange={setOpen}>
                <DialogContent className="rounded-sm" data-testid="run-analysis-dialog">
                    <DialogHeader>
                        <DialogTitle className="font-display">Run cultural analysis</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-3">
                        <div>
                            <div className="overline mb-1.5">Model</div>
                            <Select value={model} onValueChange={setModel}>
                                <SelectTrigger data-testid="model-select" className="rounded-sm"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    {MODELS.map((m) => (<SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>))}
                                </SelectContent>
                            </Select>
                        </div>
                        <div>
                            <div className="overline mb-1.5">Optional title hint</div>
                            <Input data-testid="title-hint-input" value={titleHint} onChange={(e) => setTitleHint(e.target.value)} placeholder="e.g. Reddit r/dating swipe fatigue thread" className="rounded-sm" />
                        </div>
                        <p className="text-[11px] text-muted-foreground">Analyzing {pendingIds.length} signals. Typically 20–60 seconds.</p>
                    </div>
                    <DialogFooter>
                        <Button
                            data-testid="run-analysis-confirm"
                            onClick={runAnalysis}
                            disabled={running}
                            className="rounded-sm"
                        >
                            {running ? <ArrowsClockwise className="animate-spin mr-2" size={14} /> : <Brain size={14} className="mr-2" />}
                            {running ? "Analyzing…" : "Run analysis"}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            <Dialog open={saveOpen} onOpenChange={setSaveOpen}>
                <DialogContent className="rounded-sm" data-testid="save-insight-dialog">
                    <DialogHeader>
                        <DialogTitle className="font-display flex items-center gap-2"><BookOpen size={18} weight="duotone" /> Save to Insights</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-3">
                        <div>
                            <div className="overline mb-1.5">Title</div>
                            <Input data-testid="insight-title-input" value={insightTitle} onChange={(e) => setInsightTitle(e.target.value)} className="rounded-sm" />
                        </div>
                        <div>
                            <div className="overline mb-1.5">Observation</div>
                            <textarea
                                data-testid="insight-text-input"
                                rows={4}
                                value={insightText}
                                onChange={(e) => setInsightText(e.target.value)}
                                className="w-full rounded-sm border border-input p-2 text-sm bg-background focus:outline-none focus:ring-1 focus:ring-primary"
                            />
                        </div>
                    </div>
                    <DialogFooter>
                        <Button data-testid="confirm-save-insight" onClick={saveInsight} className="rounded-sm">Save Insight</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </>
    );
}
